import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import usFlag from '@/assets/flags/us.png';
import euFlag from '@/assets/flags/eu.png';
import gbFlag from '@/assets/flags/gb.png';
import caFlag from '@/assets/flags/ca.png';
import auFlag from '@/assets/flags/au.png';
import seFlag from '@/assets/flags/se.png';
import noFlag from '@/assets/flags/no.png';
import dkFlag from '@/assets/flags/dk.png';
import chFlag from '@/assets/flags/ch.png';

export interface Market {
  slug: string;
  name: string;
  currency: string;
  currencySymbol: string;
  language: string;
  flag: string;
}

export const MARKETS: Market[] = [
  { slug: 'gettested-us', name: 'United States', currency: 'USD', currencySymbol: '$', language: 'en', flag: usFlag },
  { slug: 'gettested-uk', name: 'United Kingdom', currency: 'GBP', currencySymbol: '£', language: 'en', flag: gbFlag },
  { slug: 'gettested-eu', name: 'Europe', currency: 'EUR', currencySymbol: '€', language: 'en', flag: euFlag },
  { slug: 'gettested-ca', name: 'Canada', currency: 'CAD', currencySymbol: 'CA$', language: 'en', flag: caFlag },
  { slug: 'gettested-au', name: 'Australia', currency: 'AUD', currencySymbol: 'A$', language: 'en', flag: auFlag },
  { slug: 'gettested-se', name: 'Sweden', currency: 'SEK', currencySymbol: 'kr', language: 'sv', flag: seFlag },
  { slug: 'gettested-no', name: 'Norway', currency: 'NOK', currencySymbol: 'kr', language: 'no', flag: noFlag },
  { slug: 'gettested-dk', name: 'Denmark', currency: 'DKK', currencySymbol: 'kr', language: 'da', flag: dkFlag },
  { slug: 'gettested-ch', name: 'Switzerland', currency: 'CHF', currencySymbol: 'CHF', language: 'de', flag: chFlag },
];

interface MarketContextType {
  currentMarket: Market;
  setMarket: (marketSlug: string, clearCart?: boolean) => void;
  markets: Market[];
  formatPrice: (price: number) => string;
  getMarketSlug: () => string;
}

const MarketContext = createContext<MarketContextType | undefined>(undefined);

const MARKET_STORAGE_KEY = 'selectedMarket';

function getDefaultMarket(): Market {
  // Try to get from localStorage first
  const stored = localStorage.getItem(MARKET_STORAGE_KEY);
  if (stored) {
    const found = MARKETS.find(m => m.slug === stored);
    if (found) return found;
  }
  
  // Fall back to domain-based detection
  const hostname = window.location.hostname;
  
  if (hostname.includes('gettested.co.uk') || hostname.includes('gettested-uk')) {
    return MARKETS.find(m => m.slug === 'gettested-uk') || MARKETS[0];
  }
  if (hostname.includes('gettested.eu') || hostname.includes('gettested-eu')) {
    return MARKETS.find(m => m.slug === 'gettested-eu') || MARKETS[0];
  }
  if (hostname.includes('gettested.se') || hostname.includes('gettested-se')) {
    return MARKETS.find(m => m.slug === 'gettested-se') || MARKETS[0];
  }
  if (hostname.includes('gettested.ca') || hostname.includes('gettested-ca')) {
    return MARKETS.find(m => m.slug === 'gettested-ca') || MARKETS[0];
  }
  if (hostname.includes('gettested.au') || hostname.includes('gettested-au')) {
    return MARKETS.find(m => m.slug === 'gettested-au') || MARKETS[0];
  }
  if (hostname.includes('gettested.no') || hostname.includes('gettested-no')) {
    return MARKETS.find(m => m.slug === 'gettested-no') || MARKETS[0];
  }
  if (hostname.includes('gettested.dk') || hostname.includes('gettested-dk')) {
    return MARKETS.find(m => m.slug === 'gettested-dk') || MARKETS[0];
  }
  if (hostname.includes('gettested.ch') || hostname.includes('gettested-ch')) {
    return MARKETS.find(m => m.slug === 'gettested-ch') || MARKETS[0];
  }
  
  // Default to US
  return MARKETS[0];
}

export function MarketProvider({ children, onClearCart }: { children: ReactNode; onClearCart?: () => void }) {
  const [currentMarket, setCurrentMarket] = useState<Market>(getDefaultMarket);
  const queryClient = useQueryClient();

  const setMarket = useCallback((marketSlug: string, clearCart: boolean = true) => {
    const market = MARKETS.find(m => m.slug === marketSlug);
    if (!market) return;
    
    // Save to localStorage
    localStorage.setItem(MARKET_STORAGE_KEY, marketSlug);
    
    // Update state
    setCurrentMarket(market);
    
    // Clear cart if requested
    if (clearCart && onClearCart) {
      onClearCart();
    }
    
    // Invalidate all CMS queries to refetch with new market pricing
    queryClient.invalidateQueries({ queryKey: ['cms-products'] });
    queryClient.invalidateQueries({ queryKey: ['cms-product'] });
    queryClient.invalidateQueries({ queryKey: ['cms-categories'] });
    queryClient.invalidateQueries({ queryKey: ['cms-site-settings'] });
    
    // Show confirmation
    toast.success(`Market changed to ${market.name}`, {
      description: `Prices now shown in ${market.currency}${clearCart ? '. Cart cleared.' : ''}`
    });
  }, [queryClient, onClearCart]);

  const formatPrice = useCallback((price: number): string => {
    if (!price && price !== 0) return '';
    
    const { currency, currencySymbol } = currentMarket;
    
    // Format with proper decimals (0 for JPY-like currencies)
    const decimals = ['JPY'].includes(currency) ? 0 : 2;
    const formatted = price.toFixed(decimals);
    
    // Place symbol before or after based on currency convention
    if (['EUR', 'SEK', 'NOK', 'DKK'].includes(currency)) {
      return `${formatted} ${currencySymbol}`;
    }
    
    return `${currencySymbol}${formatted}`;
  }, [currentMarket]);

  const getMarketSlug = useCallback(() => currentMarket.slug, [currentMarket.slug]);

  return (
    <MarketContext.Provider value={{ 
      currentMarket, 
      setMarket, 
      markets: MARKETS, 
      formatPrice,
      getMarketSlug
    }}>
      {children}
    </MarketContext.Provider>
  );
}

export function useMarket() {
  const context = useContext(MarketContext);
  if (context === undefined) {
    throw new Error('useMarket must be used within a MarketProvider');
  }
  return context;
}

// For backward compatibility with existing code
export function useCurrency() {
  const { currentMarket, formatPrice } = useMarket();
  return {
    currency: currentMarket.currency,
    setCurrency: () => {}, // No-op for compatibility
    formatPrice: (price: number, _fromCurrency?: string) => formatPrice(price),
    convertPrice: (price: number, _fromCurrency?: string) => price, // No conversion - prices come from CMS
    rates: {}
  };
}
